import { useState, useRef, useEffect } from 'react';

export default function TestimonialsSection() {
  const testimonialImages = [
    'https://i.ibb.co/84ScTwX9/IMG-2737.png',
    'https://i.ibb.co/8g9GWXZ7/IMG-2738.png',
    'https://i.ibb.co/XfB0hKxN/IMG-2739.png',
    'https://i.ibb.co/Mx7WkccK/IMG-2740.png',
    'https://i.ibb.co/4ZLZc2KB/IMG-2741.png',
    'https://i.ibb.co/Kp5ccX9C/IMG-2742.png',
    'https://static.readdy.ai/image/36990eb3aa57e3c7d396ac7e72f77043/98ea0ee372d03764eeda9b9fb8c0c47a.png'
  ];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const [desktopCarouselIndex, setDesktopCarouselIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDesktopAutoPlaying, setIsDesktopAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const desktopAutoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Mobile slider autoplay
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
      }, 2000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, testimonialImages.length]);

  // Desktop carousel autoplay
  useEffect(() => {
    if (isDesktopAutoPlaying) {
      desktopAutoPlayRef.current = setInterval(() => {
        setDesktopCarouselIndex((prev) => {
          const maxIndex = testimonialImages.length - 3;
          return prev >= maxIndex ? 0 : prev + 1;
        });
      }, 2000);
    }

    return () => {
      if (desktopAutoPlayRef.current) {
        clearInterval(desktopAutoPlayRef.current);
      }
    };
  }, [isDesktopAutoPlaying, testimonialImages.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
    }

    if (touchStart - touchEnd < -75) {
      setMobileSlideIndex((prev) => (prev - 1 + testimonialImages.length) % testimonialImages.length);
    }

    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const goToMobileSlide = (index: number) => {
    setMobileSlideIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const nextMobileSlide = () => {
    setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const prevMobileSlide = () => {
    setMobileSlideIndex((prev) => (prev - 1 + testimonialImages.length) % testimonialImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const nextDesktopSlide = () => {
    setDesktopCarouselIndex((prev) => {
      const maxIndex = testimonialImages.length - 3;
      return prev >= maxIndex ? 0 : prev + 1;
    });
    setIsDesktopAutoPlaying(false);
    setTimeout(() => {
      setIsDesktopAutoPlaying(true);
    }, 3000);
  };

  const prevDesktopSlide = () => {
    setDesktopCarouselIndex((prev) => {
      const maxIndex = testimonialImages.length - 3;
      return prev <= 0 ? maxIndex : prev - 1;
    });
    setIsDesktopAutoPlaying(false);
    setTimeout(() => {
      setIsDesktopAutoPlaying(true);
    }, 3000);
  };

  const openLightbox = (index: number) => {
    setCurrentLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextLightboxImage = () => {
    setCurrentLightboxIndex((prev) => 
      prev === testimonialImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevLightboxImage = () => {
    setCurrentLightboxIndex((prev) => 
      prev === 0 ? testimonialImages.length - 1 : prev - 1
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextLightboxImage();
    if (e.key === 'ArrowLeft') prevLightboxImage();
  };

  return (
    <>
      {/* Section spacer */}
      <div className="py-8" style={{ backgroundColor: '#fdf9f4' }}></div>
      
      <section 
        id="testimonials" 
        className="py-16 lg:py-20 relative overflow-hidden"
        style={{ backgroundColor: '#1c1208' }}
      >
        {/* Subtle artistic texture overlay */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" viewBox="0 0 1200 800" className="absolute inset-0">
            <defs>
              <pattern id="artistic-texture" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                {/* Fine abstract lines */}
                <path 
                  d="M20,100 Q60,40 100,100 T180,100" 
                  fill="none" 
                  stroke="#F5F3EF" 
                  strokeWidth="0.5" 
                  opacity="0.3"
                />
                <path 
                  d="M40,60 Q80,120 120,60 T200,60" 
                  fill="none" 
                  stroke="#F5F3EF" 
                  strokeWidth="0.3" 
                  opacity="0.4"
                />
                <path 
                  d="M10,140 Q50,180 90,140 T170,140" 
                  fill="none" 
                  stroke="#F5F3EF" 
                  strokeWidth="0.4" 
                  opacity="0.2"
                />
                {/* Soft organic shapes */}
                <circle cx="30" cy="30" r="1" fill="#F5F3EF" opacity="0.2"/>
                <circle cx="150" cy="80" r="0.8" fill="#F5F3EF" opacity="0.15"/>
                <circle cx="80" cy="160" r="1.2" fill="#F5F3EF" opacity="0.25"/>
                {/* Abstract wave elements */}
                <path 
                  d="M0,180 Q50,160 100,180 T200,180" 
                  fill="none" 
                  stroke="#F5F3EF" 
                  strokeWidth="0.2" 
                  opacity="0.3"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#artistic-texture)"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-10">
            <h2 
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ 
                letterSpacing: '0.02em',
                lineHeight: '1.3',
                color: '#F5F3EF',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              What My Clients Say
            </h2>
            <p 
              className="text-xl max-w-3xl mx-auto"
              style={{ 
                color: '#CFC8C2',
                lineHeight: '1.6'
              }}
            >
              Stories of transformation through creative healing
            </p>
          </div>
          
          {/* Mobile Fullscreen Slider - Only visible on mobile */}
          <div className="lg:hidden relative w-full mb-10">
            <div 
              className="relative overflow-hidden -mx-6"
              style={{
                height: 'calc(100vh - 200px)',
                minHeight: '500px',
                maxHeight: '800px'
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div 
                className="flex h-full transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${mobileSlideIndex * 100}%)` }}
              >
                {testimonialImages.map((image, index) => (
                  <div
                    key={index}
                    className="min-w-full flex-shrink-0 h-full flex items-center justify-center"
                    style={{
                      backgroundColor: '#1c1208'
                    }}
                  >
                    <img
                      src={image}
                      alt={`Client testimonial ${index + 1}`}
                      className="max-w-full max-h-full w-auto h-auto cursor-pointer"
                      style={{
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                      onClick={() => openLightbox(index)}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows - Floating on top of image */}
              <button
                onClick={prevMobileSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                style={{ 
                  backgroundColor: 'rgba(245, 243, 239, 0.2)',
                  border: '1px solid rgba(245, 243, 239, 0.4)',
                  backdropFilter: 'blur(10px)'
                }}
                aria-label="Previous testimonial"
              >
                <i 
                  className="ri-arrow-left-line text-xl"
                  style={{ color: '#F5F3EF' }}
                ></i>
              </button>
              
              <button
                onClick={nextMobileSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                style={{ 
                  backgroundColor: 'rgba(245, 243, 239, 0.2)',
                  border: '1px solid rgba(245, 243, 239, 0.4)',
                  backdropFilter: 'blur(10px)'
                }}
                aria-label="Next testimonial"
              >
                <i 
                  className="ri-arrow-right-line text-xl"
                  style={{ color: '#F5F3EF' }}
                ></i>
              </button>
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonialImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToMobileSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    index === mobileSlideIndex 
                      ? 'scale-125' 
                      : 'hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: index === mobileSlideIndex ? '#D8892B' : 'rgba(245, 243, 239, 0.4)',
                    boxShadow: index === mobileSlideIndex 
                      ? '0 0 8px rgba(216, 137, 43, 0.6)' 
                      : 'none'
                  }}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop Horizontal Carousel - Only visible on desktop */}
          <div className="hidden lg:block max-w-6xl mx-auto mb-10 relative">
            {/* Navigation Arrows */}
            <button
              onClick={prevDesktopSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              aria-label="Previous testimonials"
            >
              <i 
                className="ri-arrow-left-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>
            
            <button
              onClick={nextDesktopSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              aria-label="Next testimonials"
            >
              <i 
                className="ri-arrow-right-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>

            {/* Carousel Container */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out gap-6 lg:gap-8"
                style={{ 
                  transform: `translateX(-${desktopCarouselIndex * (100 / 3 + 2.67)}%)` 
                }}
              >
                {testimonialImages.map((image, index) => (
                  <div
                    key={index}
                    className="group cursor-pointer relative flex-shrink-0"
                    style={{ width: 'calc(33.333% - 1.33rem)' }}
                    onClick={() => openLightbox(index)}
                  >
                    <div 
                      className="relative rounded-lg overflow-hidden transition-all duration-500 group-hover:scale-[1.03]"
                      style={{
                        backgroundColor: '#1c1208',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <img
                        src={image}
                        alt={`Client testimonial ${index + 1}`}
                        className="w-full h-auto object-contain"
                        style={{ 
                          minHeight: '300px',
                          maxHeight: '400px'
                        }}
                      />
                      
                      {/* Subtle hover overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <p 
              className="text-lg mb-4"
              style={{ 
                color: '#F5F3EF',
                lineHeight: '1.6'
              }}
            >
              Want to experience it for yourself?
            </p>
            <button 
              className="px-10 py-4 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer group relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(135deg, #D8892B 0%, #E67E22 50%, #D35400 100%)',
                boxShadow: '0 8px 24px rgba(216, 137, 43, 0.3), 0 0 0 1px rgba(255,255,255,0.1)',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
              onClick={() => {
                const element = document.getElementById('services-pricing');
                if (element) {
                  const headerOffset = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <span className="relative z-10">Book Your Session</span>
              {/* Hover glow effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                  boxShadow: '0 0 30px rgba(216, 137, 43, 0.6)'
                }}
              ></div>
            </button>
          </div>
        </div>

        {/* Full-screen Lightbox */}
        {lightboxOpen && (
          <div 
            ref={lightboxRef}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(28, 18, 8, 0.95)' }}
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 z-60 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-close-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>

            {/* Navigation arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevLightboxImage();
              }}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-60 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-arrow-left-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextLightboxImage();
              }}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-60 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-arrow-right-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>

            {/* Image container */}
            <div 
              className="max-w-5xl max-h-[90vh] mx-auto px-20"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={testimonialImages[currentLightboxIndex]}
                alt={`Client testimonial ${currentLightboxIndex + 1}`}
                className="w-full h-auto object-contain rounded-lg"
                style={{
                  maxHeight: '90vh',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
                }}
              />
              
              {/* Image info */}
              <div className="text-center mt-6">
                <p 
                  className="text-sm"
                  style={{ 
                    color: '#CFC8C2'
                  }}
                >
                  From Instagram • {currentLightboxIndex + 1} of {testimonialImages.length}
                </p>
              </div>
            </div>

            {/* Dots indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
              {testimonialImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentLightboxIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    index === currentLightboxIndex 
                      ? 'scale-125' 
                      : 'hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: index === currentLightboxIndex ? '#D8892B' : 'rgba(245, 243, 239, 0.4)',
                    boxShadow: index === currentLightboxIndex 
                      ? '0 0 8px rgba(216, 137, 43, 0.6)' 
                      : 'none'
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
